// Shared "doctor profile" taxonomy - used by every place that edits the
// profile fields common to every role alike (status, topic interests):
// AttendeeSignup, AttendeeProfile (the one universal profile editor, used by
// speakers too), and the admin SpeakerDetail/UserManagement views. Single
// source of truth so these can't drift apart the way the old per-file
// STATUSES/INTERESTS copies did.
//
// Canonical values are stored as plain strings (JSON array in the DB column,
// see User.java) - translate for display via the `speaker.role.${status}`
// and `speaker.interest.${value}` i18n keys, same convention this app
// already used for speakers.

export const STATUSES = ['DOCTOR', 'PARTICIPANT', 'SPEAKER', 'GUIDE', 'REVIEWER', 'ADVISORY_CANDIDATE'];

// Interests are stored flat (just the leaf value) - categories are purely a
// display/grouping aid, not a DB concept. Must match the enum in
// OpenAiSpeakerEnrichmentClient.outputFormat() on the backend.
export const INTEREST_CATEGORIES = [
  {
    key: 'CAD_ACS_INTERVENTION',
    labelId: 'interestCategory.cadAcsIntervention',
    labelDefault: 'CAD / ACS / Intervention',
    interests: ['Coronary artery disease', 'Acute coronary syndrome', 'PCI', 'Stents', 'Antithrombotic therapy', 'Secondary prevention'],
  },
  {
    key: 'HEART_FAILURE',
    labelId: 'interestCategory.heartFailure',
    labelDefault: 'Heart Failure',
    interests: ['HFrEF', 'HFpEF', 'Heart failure devices', 'Telemonitoring', 'Care pathways'],
  },
  {
    key: 'STRUCTURAL_HEART_VALVES',
    labelId: 'interestCategory.structuralHeartValves',
    labelDefault: 'Structural Heart / Valves',
    interests: ['TAVI', 'Mitral valve', 'TMVR', 'Tricuspid valve', 'Heart team'],
  },
  {
    key: 'RHYTHMOLOGY',
    labelId: 'interestCategory.rhythmology',
    labelDefault: 'Rhythmology',
    interests: ['Atrial fibrillation', 'Ablation', 'Anticoagulation', 'Pacemakers & ICDs', 'Wearables'],
  },
  {
    key: 'PREVENTION',
    labelId: 'interestCategory.prevention',
    labelDefault: 'Prevention',
    interests: ['Hypertension', 'Lipid management', 'Diabetes', 'Obesity', 'Lifestyle medicine', 'Public health'],
  },
  {
    key: 'CARDIAC_SURGERY',
    labelId: 'interestCategory.cardiacSurgery',
    labelDefault: 'Cardiac Surgery',
    interests: ['CABG', 'Valve surgery', 'Aortic surgery', 'Hybrid procedures', 'Surgical outcomes'],
  },
  {
    key: 'IMAGING',
    labelId: 'interestCategory.imaging',
    labelDefault: 'Imaging',
    interests: ['Echocardiography', 'Cardiac CT', 'Cardiac MRI', 'Nuclear cardiology', 'AI in imaging'],
  },
  {
    key: 'AI_DIGITAL_HEALTH',
    labelId: 'interestCategory.aiDigitalHealth',
    labelDefault: 'AI / Digital Health',
    interests: ['Artificial intelligence', 'Automation', 'Clinical decision support', 'Patient communication'],
  },
  {
    key: 'WOMEN_AND_HEART',
    labelId: 'interestCategory.womenAndHeart',
    labelDefault: 'Women & Heart',
    interests: ['Gender medicine', 'Prevention in women', 'Pregnancy-related cardiovascular risk'],
  },
  {
    key: 'HEALTH_ECONOMICS_POLICY',
    labelId: 'interestCategory.healthEconomicsPolicy',
    labelDefault: 'Health Economics / Policy',
    interests: ['Care delivery', 'Reimbursement', 'System innovation', 'Guidelines'],
  },
  {
    key: 'EDUCATION_YOUNG_SCIENCE',
    labelId: 'interestCategory.educationYoungScience',
    labelDefault: 'Education / Young Science',
    interests: ['Mentoring', 'Research skills', 'Science communication', 'DFP/CME'],
  },
];

export const INTERESTS = INTEREST_CATEGORIES.flatMap(c => c.interests);

export const MIN_INTERESTS = 3;
